import { ComplianceReportDescription } from '@herbjoy/apex-api-models';
import { IQService, IComponentOptions } from 'angular';
import moment from 'moment';
import { Failed } from '../../core/enum/job-status';
import { ReportJobService, ReportJobTracker, ReportJobVM } from '../../core/services';
import { UtilityService } from '../../core/services/utility.service';
import templateUrl from './executive-reports-dashboard.component.html';
import './executive-reports-dashboard.component.less';
import { ReportDetailsModal } from './report-details-modal.component';
import { ReportJobRequest } from './request-report.component';


export const ExecutiveReportsDashboardComponent: IComponentOptions = {
    templateUrl,
    controller: class ExecutiveReportsDashboardComponent {
        /** Jobs that the user is currently tracking, which appear with a progress bar. */
        trackedJobs: ReportJobVM[];
        /** Jobs loaded as part of the job history. */
        jobs: ReportJobVM[];
        reportDescriptions: ComplianceReportDescription[];
        constructor(private $q: IQService, private $uibModal: any,
            private icdCorporationService: any, private reportJobService: ReportJobService,
            private reportJobTracker: ReportJobTracker,
            private utilityService: UtilityService) {
            'ngInject';
        }

        $onInit() {
            const reportDescriptionsLoaded = this.reportJobService.retrieveComplianceReportDescriptions()
                .then(reportDescriptions => this.reportDescriptions = reportDescriptions);
            const subscribedToJobs = this.reportJobTracker.retrieveTrackedJobs()
                .then(jobs => {
                    this.trackedJobs = jobs.sort(sortCreateDateDesc);
                    this.reportJobTracker.stopTrackingFinishedJobs(jobs);
                })
                .then(() => {
                    this.reportJobTracker.subscribe(jobs => {
                        // Splice together the original list of jobs with jobs that were updated.
                        this.trackedJobs = this.trackedJobs.map(function (original) {
                            const updated = jobs.filter(function (job) {
                                return job.Id === original.Id;
                            })[0];
                            return updated || original;
                        });
                        this.reportJobTracker.stopTrackingFinishedJobs(jobs);

                        // Also update jobs in the table list.
                        this.loadJobs();
                    });
                });

            const jobsLoaded = this.loadJobs();

            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(this.$q.all([reportDescriptionsLoaded, subscribedToJobs, jobsLoaded]), "Initial load failed")
            );
        }

        onViewDetails({ job }) {
            job.active = true;
            this.$uibModal.open({
                ...ReportDetailsModal,
                resolve: {
                    job: () => this.$q.all(!job.IcdCorporations ? [] : job.IcdCorporations.map(id =>
                        // Use list response to reduce data draw. TODO: Support multi-ID filter in list API
                        this.icdCorporationService.retrieveIcdCorporations({ IcdCorporationID: id })
                            .then(corporations => corporations[0].ClientName)
                            // In case of failed lookups, include the ID directly.
                            .catch(_ => id)))
                        .then(icdCorporations => ({
                            ...job,
                            IcdCorporations: icdCorporations,
                            ReportArguments: !job.ReportArguments ? [] : Object.keys(job.ReportArguments)
                                .map(name => ({
                                    name,
                                    value: job.ReportArguments[name]
                                }))
                        }))
                }
            }).result.catch(() => { }).then(() => job.active = false);
        }

        onDismissJob(job) {
            this.reportJobTracker.stopTrackingJob(job.Id);
            this.trackedJobs = this.trackedJobs.filter(p => p.Id !== job.Id);
        }

        onCancelJob({ job }) {
            if (!confirm("The queued report will be cancelled. Note that if processing has already started, " +
                "the job in progress will continue. Click OK to proceed with cancellation.")) {
                return;
            }
            const jobCancelled = this.reportJobService.cancelReportJob(job.Id)
                .then(() => {
                    job.JobStatus = Failed;
                    this.reportJobTracker.stopTrackingJob(job.Id)
                    return this.loadJobs();
                });

            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(jobCancelled, "Cancelling report failed")
            )
        }

        onSubmitRequest({ reportJob }: { reportJob: ReportJobRequest }) {
            const request = {
                ReportType: reportJob.reportType,
                ReportOutputFormat: reportJob.OutputFormat.Id,
                Reports: reportJob.reports,
                StartDate: moment(reportJob.startDate).format("YYYY-MM-DD"),
                EndDate: moment(reportJob.endDate).format("YYYY-MM-DD"),
                IcdCorporations: reportJob.icdCorporations.length ? reportJob.icdCorporations : null,
                ReportArguments: !reportJob.reportArguments ? {} : reportJob.reportArguments,
            }

            const created = this.reportJobService.createReportJob(request);

            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(created, "Failed to create request")
            )

            created.then(job => {
                this.reportJobTracker.trackNewJob(job.Id);
                this.trackedJobs.unshift(job);
                this.loadJobs();
            });
        }

        loadJobs() {
            // Load all jobs for the report history
            return this.reportJobService.retrieveReportJobs({ reportType: "Compliance" })
                .then(jobs => this.jobs = jobs.sort(sortCreateDateDesc));
        }
    }
}

function sortCreateDateDesc(a, b) {
    return moment(a.CreateDateTime).isBefore(b.CreateDateTime) ? 1 : -1;
}
